import { Intent } from '@blueprintjs/core';
import { captureException } from '@sentry/browser';
import { useContext, useEffect, useState } from 'react';

import { apiClient } from '@f/api-client';
import { SciFinderAuthContext } from '@f/context';
import { sessionStorageClient } from '@f/services/storage/wrapper';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';

const CAS_NUMBER_KEY = 'scifinder_cas_number';

const storage = sessionStorageClient;

function showToast(message: string, intent: Intent) {
  AppToaster.show(
    {
      message,
      intent,
    },
    'scifinderSupplierError'
  );
}

export const useSciFinderSupplier = () => {
  const { accessToken, isAuthenticating, authenticate } = useContext(SciFinderAuthContext);
  const [isLoading, setLoading] = useState(false);

  const openSuppliers = async (casNumber: string) => {
    setLoading(true);

    if (!accessToken) {
      storage.setItem(CAS_NUMBER_KEY, casNumber);
      void authenticate();
      return;
    }

    try {
      const { status, body } = await apiClient.startingMaterials.fetchSuppliersLink({
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: { casNumber },
        redirectUnauthorized: false,
      });

      if (status !== 200) {
        throw new Error('Failed to fetch suppliers link.', { cause: status });
      }

      if (body.link === null) {
        showToast('No suppliers have been found.', Intent.WARNING);
        return;
      }

      window.open(body.link as string, '_blank');
    } catch (error: any) {
      if (error.cause === 401) {
        storage.setItem(CAS_NUMBER_KEY, casNumber);
        void authenticate();
        return;
      }

      const toastMessage = error instanceof Error ? error.message : (error as string);
      showToast(toastMessage, Intent.DANGER);

      captureException(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const casNumber = storage.getItem(CAS_NUMBER_KEY);

    if (!casNumber) {
      return;
    }

    storage.removeItem(CAS_NUMBER_KEY);
    void openSuppliers(casNumber);
  }, [accessToken]);

  return { isLoading: isLoading || isAuthenticating, openSuppliers } as const;
};
